import React, { PureComponent, useState, useEffect, forwardRef } from 'react';
import styled from 'styled-components';
import { Field, FieldProps } from 'formik';

// Icons
import Icon from '@mdi/react';
import { mdiSend, mdiCheck, mdiAlertCircle, mdiMagnify } from '@mdi/js';

import {
  Paper,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  FilledInput,
  OutlinedInput,
  FormHelperText,
  InputAdornment,
  CircularProgress,
  Button,
  InputBase
} from '@material-ui/core';

import { colors, din, dinCondensed, mono, easeTransition } from '../../styles/variables';
import { isNumeric } from '../../util/util';
import { px, fade } from '../../util/style';
import without from '../../util/without';

//
// TextRow
//
const TextRowContainer = styled.p`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  margin: ${px(4)} 0;
`;

const TextLabel = styled.span`
  font-size: ${px(16)};
  font-weight: 200;
`;

interface TextValueProps {
  empty?: boolean;
}
const TextValue = styled.span<TextValueProps>`
  font-size: ${px(16)};
  font-weight: 600;
  margin-left: ${px(8)};
  max-width: ${px(400)};
  text-overflow: ellipsis;
  overflow-x: hidden;

  ${props =>
    props.empty &&
    `
    color: #fff;
    background-color: ${colors.deliMustard};
    padding: ${px(4)} ${px(8)};
  `}
`;

interface TextRowProps {
  label: string;
  value?: React.ReactNode;
  children?: React.ReactNode;
}
export const TextRow = (props: TextRowProps) => (
  <TextRowContainer>
    <TextLabel>{props.label}</TextLabel>
    <TextValue empty={!props.value}>{props.value ? props.value : 'None'}</TextValue>
    {props.children}
  </TextRowContainer>
);

//
// OutputTextBox
//
interface OutlinedTextFieldProps {
  multiline?: boolean;
  mono?: boolean;
  extrawide?: boolean;
  empty?: boolean;
}
const OutlinedTextField = styled(
  without('multiline', 'mono', 'extrawide', 'empty', 'endAdornment')(TextField)
)<OutlinedTextFieldProps>`
  width: ${px(160)};
  margin-top: 0;
  margin-right: ${px(16)};

  & label {
    font-family: ${din};
    font-size: ${px(16)};
    color: ${colors.gray87};
    background-color: #fff;
    padding: 0 ${px(6)};
    z-index: 15;
    transform: translate(13px, -6px) scale(0.85) !important;
  }

  & input {
    font-family: ${din};
    color: ${colors.gray87};
    background-color: ${colors.milk2};
  }

  & fieldset {
    background-color: transparent;
    border-radius: 0;
    border: none;
    border-top: 1px solid ${colors.gray13};
    border-bottom: 1px solid ${colors.gray13};
    z-index: 10;
  }

  & p {
    color: ${colors.gray87};
  }

  ${props =>
    props.multiline &&
    `
    width: 100%;
    margin: 0;

    &>div {
      padding: ${px(8)} ${px(4)} ${px(8)} ${px(16)};
    }

    & fieldset {
      border: 1px solid ${colors.gray13};
    }
  `}

  ${props =>
    props.mono &&
    `
    & textarea {
      color: ${colors.gray87};
      font-family: ${mono};
      font-weight: 400;
    }
  `}

  ${props =>
    props.extrawide &&
    `
    min-width: 50vw;
    width: 50vw;
  `}

  ${props =>
    props.empty &&
    `
    & input {
      color: ${colors.deliMustard};
    }
  `}
`;

type PlainTextFieldProps = {
  empty?: boolean;
  error?: boolean;
};
const PlainTextField = styled(without('empty', 'error', 'endAdornment')(TextField))<PlainTextFieldProps>`
  width: 80%;
  margin-top: 0;
  margin-right: ${px(16)};

  & label {
    font-family: ${din};
    font-size: ${px(16)};
    color: ${colors.gray87};
    background-color: #fff;
    padding-right: ${px(6)};
    transform: translate(0px, -6px) scale(0.85) !important;
  }

  & div::before {
    border-bottom: none !important;
  }

  & input {
    font-family: ${din};
    font-weight: 600;
    color: ${colors.gray87};
    text-overflow: ellipsis;
    padding-left: ${px(8)};
  }

  & fieldset {
    border: none;
  }

  & p {
    color: ${colors.gray87};
  }

  ${props =>
    props.empty &&
    `
    & label {
      color: #fff;
      background-color: ${colors.deliMustard};
      padding: ${px(4)} ${px(8)};
    }

    & input {
      font-weight: 600;
      color: ${colors.deliMustard};
    }
  `}

  ${props =>
    props.error &&
    `
    & label {
      color: #fff !important;
      background-color: ${colors.errorClapton};
      padding: ${px(4)} ${px(8)};
    }

    & div::after {
      border-bottom-color: ${colors.errorClapton} !important;
    }
  `}
`;

interface OutputTextBoxProps {
  precision?: number;
  value: number | string;
  plain?: boolean;
  id?: string;
  multiline?: boolean;
  mono?: boolean;
  extrawide?: boolean;
  label: string;
  error?: any;
  subtext?: string;
  startIcon?: any;
  endIcon?: any;
}
export const OutputTextBox = (props: OutputTextBoxProps) => {
  const precision = props.precision ? props.precision : 1;
  let value = isNumeric(props.value) ? parseFloat(props.value as any).toFixed(precision) : (props.value as string);
  if (!value) value = '';

  return (
    <>
      {props.plain ? (
        <PlainTextField
          id={props.id}
          multiline={props.multiline}
          rowsMax={10}
          //@ts-expect-error ?
          extrawide={props.extrawide}
          label={props.label}
          value={value ? value.toString() : 'None'}
          empty={value.toString().length < 1}
          error={props.error}
          helperText={props.subtext}
          margin="normal"
          variant="standard"
          InputProps={{
            readOnly: true,
            disabled: true,
            startAdornment: props.startIcon ? (
              <InputAdornment position="start">{props.startIcon}</InputAdornment>
            ) : (
              false
            ),
            endAdornment: props.endIcon ? <InputAdornment position="end">{props.endIcon}</InputAdornment> : false
          }}
        />
      ) : (
        <OutlinedTextField
          id={props.id}
          multiline={props.multiline}
          rowsMax={10}
          mono={props.mono}
          extrawide={props.extrawide}
          label={props.label}
          value={value ? value.toString() : 'None'}
          empty={value.toString().length < 1}
          helperText={props.subtext}
          margin="normal"
          variant="outlined"
          InputProps={{
            readOnly: true,
            disabled: true,
            startAdornment: props.startIcon ? (
              <InputAdornment position="start">{props.startIcon}</InputAdornment>
            ) : (
              false
            ),
            endAdornment: props.endIcon ? <InputAdornment position="end">{props.endIcon}</InputAdornment> : false
          }}
        />
      )}
    </>
  );
};

//
// IndicatorTextBox
//
const IndicatorWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const IndicatorContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`;

const IndicatorTitle = styled.span`
  position: relative;
  font-family: ${din};
  font-weight: 200;
  font-size: ${px(16)};
  color: ${colors.gray87};
  margin-bottom: ${px(2)};
`;

interface IndicatorTabProps {
  bgColor?: string;
}
const IndicatorTab = styled.div<IndicatorTabProps>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  height: ${px(48)};
  background-color: ${colors.gray13};
  padding: 0 ${px(12)};
  margin: 0;
  margin-right: -1px;
  border: 1px solid ${colors.gray45};
  border-radius: 0;
  border-top-left-radius: ${px(8)};
  border-bottom-left-radius: ${px(8)};
  z-index: 10;

  & svg {
    height: ${px(24)};
    color: ${colors.gray45};
  }

  ${props =>
    props.color &&
    `
    & svg {
      color: ${props.color};
    }
  `}

  ${props =>
    props.bgColor &&
    `
    background-color: ${props.bgColor};
  `}
`;

interface IndicatorContentProps {
  label?: string;
  compact?: boolean;
}
const IndicatorContent = styled.div<IndicatorContentProps>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  height: ${px(48)};
  font-family: ${din};
  font-weight: 400;
  font-size: ${px(18)};
  background-color: ${colors.white};
  padding: 0 ${px(32)};
  margin: 0;
  border: 1px solid ${colors.gray13};
  border-radius: 0;
  border-top-right-radius: ${px(8)};
  border-bottom-right-radius: ${px(8)};

  ${props =>
    props.label &&
    `
    padding-right: ${px(72)};
  `}

  ${props =>
    props.compact &&
    `
    font-family: ${dinCondensed};
    font-weight: 400;
  `}
`;

const IndicatorLabel = styled.span`
  position: absolute;
  right: 18px;
  font-family: ${dinCondensed};
  font-weight: 400;
  font-size: ${px(14)};
  color: ${colors.gray45};
  text-align: right;
`;

interface IndicatorTextBoxProps {
  title?: string;
  color?: string;
  bgColor?: string;
  icon?: React.ReactElement;
  label?: string;
  compact?: boolean;
  value?: string;
}
export const IndicatorTextBox = (props: IndicatorTextBoxProps) => {
  return (
    <IndicatorWrapper>
      <IndicatorTitle>{props.title ? props.title : false}</IndicatorTitle>
      <IndicatorContainer>
        <IndicatorTab color={props.color} bgColor={props.bgColor}>
          {props.icon ? props.icon : false}
        </IndicatorTab>

        <IndicatorContent label={props.label} compact={props.compact}>
          {props.value ? props.value : false}
        </IndicatorContent>

        <IndicatorLabel>{props.label ? props.label : false}</IndicatorLabel>
      </IndicatorContainer>
    </IndicatorWrapper>
  );
};

//
// InputTextBox
//
interface StyledInputLabelProps {
  static?: boolean;
  outline?: boolean;
  icon?: boolean;
}
const StyledInputLabel = styled(without('static', 'icon', 'outline')(FormLabel))<StyledInputLabelProps>`
  position: absolute;
  top: 0;
  left: 0;
  font-family: ${din};
  color: ${colors.gray87};
  height: ${px(24)};
  opacity: 1;
  transform-origin: top left;
  transform: translate(${px(13)}, ${px(10)}) scale(0.75);
  z-index: 1;
  pointer-events: none;

  &.Mui-focused {
    color: #fff;

    ${props =>
      props.static &&
      `
      color: ${colors.gray87};
    `}

    ${props =>
      props.outline &&
      `
      color: ${colors.gray87};
    `}
  }

  ${props =>
    props.icon &&
    `
    transform: translate(${px(44)}, ${px(10)}) scale(0.75);
  `}
`;

interface StyledFilledInputProps {
  minWidth?: string;
  marginRight?: string;
  edited?: boolean;
  outline?: boolean;
  error?: boolean;
  static?: boolean;
  icon?: boolean;
  extrashort?: boolean;
  short?: boolean;
  wide?: boolean;
  extrawide?: boolean;
  fullWidth?: boolean;
  code?: boolean;
  plain?: boolean;
}
const StyledFilledInput = styled(
  without(
    'static',
    'icon',
    'short',
    'extrashort',
    'wide',
    'extrawide',
    'fullWidth',
    'code',
    'edited',
    'error',
    'number',
    'minWidth',
    'plain',
    'integer',
    'marginRight',
    'marginBottom',
    'withButton',
    'loadTime',
    'outline',
    'email',
    'password'
  )(FilledInput)
)<StyledFilledInputProps>`
  font-family: ${din};
  color: ${colors.gray87};
  background-color: #fff;
  border-top-left-radius: ${px(4)};
  border-top-right-radius: ${px(4)};
  padding-left: ${px(12)};
  min-width: ${props => (props.minWidth ? props.minWidth : px(240))};
  margin: 0;
  margin-right: ${props => (props.marginRight ? props.marginRight : 0)};
  box-shadow: 0 0 0 1px ${colors.gray13};

  & svg {
    color: ${colors.gray45};
    margin-left: ${px(3)};
    margin-right: 0;
    width: ${px(20)};
    height: ${px(20)};
  }

  &::before {
    border-bottom-color: ${colors.gray87};
  }

  &::after {
    border-bottom-color: ${colors.rain};

    ${props =>
      props.edited &&
      props.outline &&
      `
      border-bottom-color: ${colors.deliMustard};
    `}

    ${props =>
      props.error &&
      props.error &&
      `
      border-bottom-color: ${colors.errorClapton};
    `}
  }

  &:hover {
    background-color: ${fade(colors.rainLight, 0.85)};

    ${props =>
      props.static &&
      `
      background-color: #fff;
    `}
  }

  &.Mui-focused {
    color: #fff;
    background-color: ${colors.rainLight};

    & svg {
      color: #fff;
    }

    ${props =>
      props.static &&
      `
      color: ${colors.gray87};
      background-color: #fff;

      & svg {
        color: ${colors.gray45};
      }

      &::after {
        border-bottom-width: 1px;
        border-bottom-color: ${colors.gray87};
      }
    `}

    ${props =>
      props.edited &&
      props.outline &&
      `
      color: ${colors.gray87};
      background-color: ${fade(colors.deliMustard, 0.85)};

      & svg {
        color: ${colors.deliMustard};
      }
    `}
  }

  ${props =>
    !props.icon &&
    `
    padding-left: 0;
  `}

  ${props =>
    props.extrashort &&
    `
    min-width: ${px(128)};
    width: ${px(128)};
  `}

  ${props =>
    props.short &&
    `
    min-width: ${px(160)};
    width: ${px(160)};
  `}

  ${props =>
    props.multiline &&
    `
    padding-left: ${px(12)};
  `}

  ${props =>
    props.wide &&
    `
    min-width: ${px(320)};
    width: ${px(320)};
  `}

  ${props =>
    props.extrawide &&
    `
    min-width: ${px(420)};
    width: ${px(420)};
  `}

  ${props =>
    props.fullWidth &&
    `
    min-width: ${px(320)};
    width: 100%;

    & .MuiFilledInput-root {
      width: 100%;
    }
  `}

  ${props =>
    props.code &&
    `
    font-family: ${mono};
    font-weight: 400;
  `}

  ${props =>
    props.edited &&
    !props.plain &&
    !props.outline &&
    `
    background-color: ${colors.deliMustard};
    box-shadow: none;

    & label {
      color: ${colors.gray87};
    }

    & svg {
      color: #fff;
    }

    &:hover {
      background-color: ${fade(colors.deliMustard, 0.15)};
    }
  `}

  ${props =>
    props.edited &&
    props.outline &&
    `
    background-color: transparent;
    box-shadow: 0 0 0 1px ${colors.deliMustard};

    &::before {
      border-bottom-color: ${colors.deliMustard};
    }

    & label {
      color: ${colors.deliMustard};
    }

    & svg {
      color: ${colors.deliMustard};
    }

    &:hover {
      background-color: ${fade(colors.deliMustard, 0.85)};
    }
  `}

  ${props =>
    props.error &&
    `
    background-color: ${colors.alleviRedPale};
    box-shadow: 0 0 0 1px ${colors.errorClapton};

    & label {
      color: ${colors.errorClapton} !important;
    }
  `}

  ${props =>
    props.disabled &&
    `
    background-color: ${colors.gray13};

    & label {
      color: ${colors.gray87};
    }

    & svg {
      color: ${colors.gray45};
    }

    & input {
      color: ${colors.gray87};
    }

    &:hover {
      background-color: ${colors.gray13};
    }
  `}
`;

interface StyledFormHelperTextProps {
  marginBottom?: number;
}
const StyledFormHelperText = styled(without('marginBottom')(FormHelperText))<StyledFormHelperTextProps>`
  font-family: ${din};
  color: ${colors.gray87};
  margin-bottom: ${px(16)};

  ${props =>
    props.marginBottom &&
    `
    margin-bottom: ${px(props.marginBottom)};
  `}
`;

interface StyledSubmitButtonProps {
  withGroup?: boolean;
  edited?: boolean;
  vertical?: boolean;
  bgColor?: string;
}
const StyledSubmitButton = styled(
  without('withGroup', 'vertical', 'edited', 'bgColor')(Button)
)<StyledSubmitButtonProps>`
  min-width: 0;
  width: ${px(62)};
  height: ${px(62)};
  padding: ${px(8)} 0;
  margin-left: ${px(16)};
  background-color: ${colors.milk2}
  border: 1px solid ${colors.gray7};
  cursor: default;
  transition: all ${easeTransition};

  &:hover {
    cursor: pointer;
    background-color: ${fade(colors.milk2, 0.15)};
  }

  &:active {
    ${props =>
      props.edited &&
      `
      transform: scale(0.95);
    `}
  }

  & svg {
    color: ${colors.gray87};
    margin: 0;
    width: ${px(24)};
    height: ${px(24)};
  }

  ${props =>
    props.withGroup &&
    `
    margin-left: ${px(0)};
  `}

  ${props =>
    props.vertical &&
    `
    margin-left: ${px(8)};
    margin-bottom: ${px(36)};
  `}

  ${props =>
    props.edited &&
    `
    cursor: pointer;
    background-color: ${colors.emerald};
    border: none;

    &:hover {
      background-color: ${fade(colors.emerald, 0.15)};
    }

    & svg {
      color: #fff;
    }
  `}

  ${props =>
    props.disabled &&
    `
    visibility: hidden;
  `}

  ${props =>
    props.bgColor &&
    `
    background-color: ${props.bgColor};
  `}
`;

interface FormInputTextBoxProps
  extends Omit<StyledFilledInputProps, 'error' | 'icon' | 'inputProps' | 'startAdorment'> {
  number?: boolean;
  email?: boolean;
  password?: boolean;
  value?: any;
  withButton?: boolean;
  disabled?: boolean;
  name: string;
  icon?: any;
  subtext?: string;
  marginBottom?: number;
  static?: boolean;
  label?: string;
  maxLength?: number;
  placeholder?: string;
}
export const FormInputTextBox = (props: FormInputTextBoxProps) => {
  let type = 'text';
  if (props.number) type = 'number';
  if (props.email) type = 'email';
  if (props.password) type = 'password';

  const { value: omitValue, ...inputProps } = props;

  return (
    <>
      <Field>
        {({ field, form }: FieldProps) => (
          <FormControl variant="filled" {...field}>
            <StyledInputLabel icon={props.icon !== undefined} static={props.static}>
              {form.errors[props.name] ? form.errors[props.name] : props.label}
            </StyledInputLabel>
            <StyledFilledInput
              error={!!form.errors[props.name]}
              icon={props.icon !== undefined}
              {...inputProps}
              inputProps={{
                type,
                maxLength: props.maxLength
              }}
              startAdornment={props.icon ? <InputAdornment position="start">{props.icon}</InputAdornment> : false}
            />
            <StyledFormHelperText marginBottom={props.marginBottom}>{props.subtext}</StyledFormHelperText>
          </FormControl>
        )}
      </Field>

      {props.withButton && !props.disabled ? (
        <StyledSubmitButton disabled={props.disabled}>
          <Icon path={mdiSend} />
        </StyledSubmitButton>
      ) : (
        false
      )}
    </>
  );
};

interface LoadingProgressProps {
  edited?: boolean;
}
const LoadingProgress = styled(without('edited')(CircularProgress))<LoadingProgressProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.gray45};

  ${props =>
    props.edited &&
    `
    color: #fff;
  `}
`;

type InputTextBoxProps = Omit<
  StyledFilledInputProps,
  | 'value'
  | 'edited'
  | 'error'
  | 'icon'
  | 'inputRef'
  | 'inputProps'
  | 'onChange'
  | 'onFocus'
  | 'onBlur'
  | 'startAdornment'
> & {
  withButton?: boolean;
  disabled?: boolean;
  outlined?: boolean;
  fullWidth?: boolean;
  icon?: any;
  static?: boolean;
  outline?: boolean;
  loadTime?: number;
  label: string;
  error?: any;
  maxLength?: number;
  marginBottom?: number;
  subtext?: string;
  changeImmediate?: boolean; //trigger the onChange in the parent as soon as a change is observed, otherwise onBlur
} & (
    | {
        number: true;
        precision?: number;
        value: number;
        min?: number;
        max?: number;
        integer?: boolean;
        onChange?: (v: number, edited: boolean) => void;
        onSubmit?: (v: number) => void;
      }
    | {
        number?: false;
        value: string;
        email?: boolean;
        password?: boolean;
        onChange?: (v: string, edited: boolean) => void;
        onSubmit?: (v: string) => void;
      }
  );
export const InputTextBox = forwardRef(
  (
    props: Omit<
      StyledFilledInputProps,
      | 'value'
      | 'edited'
      | 'error'
      | 'icon'
      | 'inputRef'
      | 'inputProps'
      | 'onChange'
      | 'onFocus'
      | 'onBlur'
      | 'startAdornment'
    > & {
      withButton?: boolean;
      disabled?: boolean;
      outlined?: boolean;
      fullWidth?: boolean;
      icon?: any;
      static?: boolean;
      outline?: boolean;
      loadTime?: number;
      label: string;
      error?: any;
      maxLength?: number;
      marginBottom?: number;
      subtext?: string;
      email?: boolean;
      password?: boolean;
      changeImmediate?: boolean; //trigger the onChange in the parent as soon as a change is observed, otherwise onBlur
      precision?: number;
      min?: number;
      max?: number;
      integer?: boolean;
      number?: false;
      value: string | number;
      onChange?: (v: string | number, edited: boolean) => void;
      onSubmit?: (v: string | number) => void;
      id?: string;
    },
    ref
  ) => {
    const [value, setValue] = useState(props.value);
    const [changed, setChanged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [typing, setTyping] = useState(false);

    const triggerLoading = (loadTime: number) => {
      setLoading(true);
      setTimeout(() => setLoading(false), loadTime);
    };

    /*eslint-disable-next-line react-hooks/exhaustive-deps */ //TODO: verify
    useEffect(() => {
      const precision = props.precision ? props.precision : -1;

      if (props.value !== value && !changed && !typing) {
        const newVal =
          precision > -1 && isNumeric(props.value) ? parseFloat(props.value as any).toFixed(precision) : props.value;
        setValue(newVal);
      } else if (changed && !typing) {
        const newVal = precision > -1 && isNumeric(value) ? parseFloat(value as any).toFixed(precision) : value;
        setValue(newVal);
      }
    });

    useEffect(() => {
      setValue(props.value);
    }, [props.value]);

    const validateInput = (i: string | number) => {
      if (props.number && isNumeric(i)) {
        i = parseFloat(i as string);
      }

      if (props.integer && isNumeric(i)) {
        i = Math.round(i as number);
      }

      if (props.min !== undefined && isNumeric(i) && (i as number) < parseFloat(props.min as TSFixMe)) {
        i = parseFloat(props.min as TSFixMe);
      }

      if (props.max !== undefined && isNumeric(i) && (i as number) > parseFloat(props.max as TSFixMe)) {
        i = parseFloat(props.max as TSFixMe);
      }

      return i;
    };

    const parseValue = () => {
      if (value === undefined) return '';

      if (props.precision! > -1 && !typing) return parseFloat(value as TSFixMe).toFixed(props.precision);

      return value;
    };

    const isEdited = () => {
      if (props.number) {
        if (Math.abs((value as number) - (props.value as number)) < Number.EPSILON) return false;
        return true;
      }
      return value !== props.value;
    };

    const callOnChange = (...args: [string | number, boolean]) => {
      if (typeof props.onChange === 'function') {
        props.onChange(...args);
      }
    };

    const callOnSubmit = (...args: [string | number]) => {
      if (typeof props.onSubmit === 'function') {
        props.onSubmit(...args);
      }
    };

    let type = 'text';
    if (props.number) type = 'number';
    if (props.email) type = 'email';
    if (props.password) type = 'password';

    const { value: omitValue, onChange: omitOnChange, onSubmit: omitOnSubmit, ...inputProps } = props;

    return (
      <>
        <FormControl variant={props.outlined ? 'outlined' : 'filled'} fullWidth={props.fullWidth}>
          <StyledInputLabel icon={props.icon !== undefined} static={props.static} outline={props.outline}>
            {props.label}
          </StyledInputLabel>
          <StyledFilledInput
            value={parseValue()}
            edited={isEdited()}
            error={props.error}
            icon={props.icon !== undefined}
            {...inputProps}
            inputRef={ref}
            inputProps={{
              type,
              maxLength: props.maxLength
            }}
            onChange={e => {
              const newVal: string | number = e.target.value;
              setValue(newVal);
              setChanged(true);
              if (props.changeImmediate) {
                callOnChange(newVal, !!value && newVal !== props.value);
              }
            }}
            onFocus={() => setTyping(true)}
            onBlur={e => {
              setTyping(false);

              let newVal: string | number = e.target.value;

              if (newVal === '' || newVal === undefined) {
                newVal = props.value;
                setChanged(false);
              } else {
                newVal = validateInput(newVal);
              }

              setValue(newVal !== undefined && newVal.toString().length > 0 ? newVal.toString() : '');

              // Call onChange from props with (value, edited)
              callOnChange(newVal, !!value && newVal !== props.value);
            }}
            startAdornment={props.icon ? <InputAdornment position="start">{props.icon}</InputAdornment> : false}
          />
          <StyledFormHelperText marginBottom={props.marginBottom} error={props.error}>
            {props.subtext}
          </StyledFormHelperText>
        </FormControl>

        {props.withButton && !props.disabled ? (
          <StyledSubmitButton
            disabled={props.disabled}
            edited={!!value && value !== props.value}
            onClick={() => {
              if (props.loadTime) {
                triggerLoading(props.loadTime);
              }
              callOnSubmit(validateInput(value));
              setValue(props.value);
              setChanged(false);
            }}
          >
            {loading ? <LoadingProgress edited={!!value && value !== props.value} /> : <Icon path={mdiSend} />}
          </StyledSubmitButton>
        ) : (
          false
        )}
      </>
    );
  }
) as React.ComponentType<InputTextBoxProps>;

//
// InputTextBoxGroup
//
interface ButtonGroupContainerProps {
  verticalSpacing?: number;
  horizontalSpacing?: number;
}
const ButtonGroupContainer = styled.div<ButtonGroupContainerProps>`
  display: flex;
  flex-flow: row nowrap;
  margin: 0;
  padding: 0;

  ${props =>
    props.verticalSpacing &&
    `
    &:not(:last-of-type) {
      margin-bottom: ${px(props.verticalSpacing)};
    }
  `}

  ${props =>
    props.horizontalSpacing &&
    `
    & .MuiFormControl-root {
      margin-right: ${px(props.horizontalSpacing)};
    }
  `}
`;

interface InputTextBoxGroupState {
  edited: number;
  values: Record<number, string | number>;
  inputRefs: React.RefObject<unknown>[];
}
interface InputTextBoxGroupProps {
  children: React.ReactElement[];
  vertical?: boolean;
  verticalSpacing?: number;
  horizontalSpacing?: number;
  withButton?: boolean;
  disabled?: boolean;
  onSubmit: (v: (string | undefined)[]) => void;
}
export class InputTextBoxGroup extends PureComponent<InputTextBoxGroupProps, InputTextBoxGroupState> {
  constructor(props: InputTextBoxGroupProps) {
    super(props);

    // Get values from child textboxes
    const initialValues: Record<number, string | number> = {};
    React.Children.map(this.props.children, (child, index) => {
      initialValues[index] = child.props.value;
    });

    this.state = {
      // Uses bitmasks to determine edited state of each textbox
      edited: 0,
      values: initialValues,
      inputRefs: [...Array(this.props.children.length)].map(() => React.createRef())
    };
  }

  handleEdit(value: string | number, edited: boolean, index: number) {
    // Set value in state
    this.setState(prevState => ({
      values: {
        ...prevState.values,
        [`${index}`]: value
      }
    }));

    // Toggle bitmask for edited/unedited textbox
    if (edited) {
      this.setState(prevState => ({
        edited: prevState.edited | (1 << index)
      }));
    } else {
      this.setState(prevState => ({
        edited: prevState.edited & ~(1 << index)
      }));
    }
  }

  generateChildren() {
    return React.Children.map(this.props.children, (child, index) => {
      return React.cloneElement(child, {
        disabled: this.props.disabled,
        onChange: (value: string | number, edited: boolean) => {
          this.handleEdit(value, edited, index);
        },
        ref: this.state.inputRefs[index]
      });
    });
  }

  render() {
    return (
      <Grid container item xs={12} direction={this.props.vertical ? 'column' : 'row'}>
        <ButtonGroupContainer
          verticalSpacing={this.props.verticalSpacing}
          horizontalSpacing={this.props.horizontalSpacing}
        >
          {/* Add disabled and onChange callback prop to all child textboxes */}
          {this.generateChildren()}

          {this.props.withButton && !this.props.disabled && (
            <StyledSubmitButton
              withGroup
              vertical={this.props.vertical}
              disabled={this.props.disabled}
              edited={this.state.edited !== 0}
              onClick={() => {
                const values = this.state.inputRefs.map(r =>
                  r.current ? ((r.current as any).value as string) : undefined
                );
                this.setState({ edited: 0 });
                this.props.onSubmit(values);
              }}
            >
              <Icon path={mdiSend} />
            </StyledSubmitButton>
          )}
        </ButtonGroupContainer>
      </Grid>
    );
  }
}

const CodeInput = styled(OutlinedInput)`
  font-family: ${mono};
  font-weight: 400;
  line-height: ${px(20)};
  width: 100%;
  margin-top: 0;
  margin-right: ${px(16)};

  &.Mui-focused {
    & fieldset {
      border: 1px solid ${colors.gray87} !important;
    }
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
`;

interface CodeInputTextBoxProps {
  status: number;
  withButton?: boolean;
  disabled?: boolean;
  icon?: any;
  label?: string;

  onChange?: () => void;
  onSubmit?: (v: string) => void;
}
export const CodeInputTextBox = (props: CodeInputTextBoxProps) => {
  const [value, setValue] = useState('');

  const callOnSubmit = (...args: [string]) => {
    if (typeof props.onSubmit === 'function') {
      props.onSubmit(...args);
    }
  };

  const statusToColor = (status: number) => {
    if (!status) return colors.milk2;

    if (status === 1) return colors.emerald;
    if (status === 2) return colors.errorClapton;

    return colors.milk2;
  };

  return (
    <StyledForm
      onSubmit={e => {
        e.preventDefault();
        callOnSubmit(value);
      }}
    >
      <FormControl variant="filled" fullWidth>
        <StyledInputLabel icon={props.icon !== undefined}>{props.label}</StyledInputLabel>
        <CodeInput
          value={value}
          disabled={props.disabled}
          onChange={e => {
            const currentValue = e.target.value ? e.target.value.toUpperCase() : undefined;
            setValue(currentValue!);
          }}
        />
      </FormControl>

      {props.withButton && !props.disabled ? (
        <StyledSubmitButton
          disabled={props.disabled}
          bgColor={statusToColor(props.status)}
          onClick={() => {
            callOnSubmit(value);
          }}
        >
          {!props.status || (props.status && props.status === 0) ? <Icon path={mdiSend} /> : false}

          {props.status && props.status === 1 ? <Icon path={mdiCheck} color={colors.white} /> : false}

          {props.status && props.status === 2 ? <Icon path={mdiAlertCircle} color={colors.white} /> : false}
        </StyledSubmitButton>
      ) : (
        false
      )}
    </StyledForm>
  );
};

const StyledPaper = styled(Paper)`
  border: 1px solid ${colors.gray13};
  border-radius: ${px(4)};
`;

const SearchInput = styled(InputBase)`
  padding-left: ${px(16)};

  & input {
    width: ${px(300)};
    font-family: ${din};
    font-size: ${px(16)};
    padding: ${px(18)} ${px(16)} ${px(18)} ${px(8)};
  }
`;

interface SearchBoxProps {
  placeholder?: string;
  onChange?: (v: string) => void;
}
export const SearchBox = (props: SearchBoxProps) => (
  <StyledPaper elevation={0}>
    <SearchInput
      placeholder={props.placeholder}
      startAdornment={<Icon path={mdiMagnify} color={colors.gray45} size={px(24)} />}
      onChange={e => (props.onChange ? props.onChange(e.currentTarget.value) : false)}
    />
  </StyledPaper>
);
